import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { EmailVerificationBanner } from './EmailVerificationBanner';
import { BusinessCapacityBanner } from './BusinessCapacityGate';
import { ResponsibleContactBanner } from './ResponsibleContactBanner';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';
import { LegalFooter } from './LegalFooter';

/**
 * Customer-portal shell — Navbar on top, Sidebar on the left, Main
 * content slot rendered via React Router's <Outlet />. Email-verification
 * banner sits between navbar and main content so it follows the user
 * across routes until they verify.
 *
 * Authenticated-only — routes that render this shell are nested under a
 * <ProtectedRoute> wrapper in App.tsx. Unauthenticated customers never
 * see the shell; they get bounced to /portal/login.
 */
export function AppShell() {
  // OCP-282 — mobile nav drawer state. Navbar's hamburger opens it; the
  // Sidebar renders the drawer + closes it on link-click / backdrop / Esc.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // Defensive: ensure the drawer never lingers across a route change.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-dvh flex-col bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <Navbar onMenuClick={() => setMobileNavOpen(true)} />
      <EmailVerificationBanner />
      {/* OCP-524 - prompts pre-clause accounts to name their responsible
          contact. Non-blocking: the portal stays fully usable, only a NEW
          contract conclusion is gated. */}
      <ResponsibleContactBanner />
      {/* OCP-534 - prompts pre-decision accounts for the Sec. 14 BGB
          business-capacity declaration, and tells a refused account that new
          orders are on hold. Same non-blocking treatment: full portal access
          stays, only a NEW contract conclusion is gated. */}
      <BusinessCapacityBanner />
      <div className="flex flex-1">
        <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
          {/* Imprint, privacy, terms and the DB-IP credit (OCP-848) on every page. */}
          <LegalFooter className="mt-10" />
        </main>
      </div>
    </div>
  );
}
